/*global navigator*/
/* eslint-disable global-require, prefer-const, no-param-reassign */
import { lazyInitialize, nonenumerable } from 'core-decorators';
import { action, computed, observable } from 'mobx';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/fr';
import { withConfig } from 'spa/Components/Context';
import { suspectInfiniteLoop } from 'spa/debug';
import { async } from 'spa/server-wait';
import I18nErrors from './errors';
import {
  safe_sprintf,
  translate, translateContext, translateContextReact, translatePlural, translatePluralContext, translatePluralReact, translateRaw,
  translateReact,
} from './functions';

const LANGUAGE_COOKIE_KEY = 'language';

const isServer = typeof window === 'undefined';

const HTTP_HOST = isServer ?
  process.env.HTTP_HOST :
  window.HTTP_HOST;

let files;
let loadLocaleFile;

const BASE_LANGUAGE = 'en-GB';
moment.locale(BASE_LANGUAGE);

const SUPPORTED_LOCALES = [
  'en-GB',
  'fr-FR',
  'en-FR',
];

const DEFAULT_LOCALES = [
  'en-GB',
  'fr-FR',
];

const LANGUAGE_FALLBACKS = {
  en: 'en-GB',
  fr: 'fr-FR',
};

const SUPPORTED_LANGUAGES = SUPPORTED_LOCALES
  .map(locale => locale.replace(/\-[^\-]*$/, ''))
  .filter((language, index, array) => array.indexOf(language) === index);

const SUPPORTED_REGIONS = SUPPORTED_LOCALES
  .map(locale => locale.replace(/^.*\-([^\-]*)$/, '$1'))
  .filter((language, index, array) => array.indexOf(language) === index);

if (__WEBPACK__) {
  const ctx = require.context('../../../../../i18n', false, /\.po$/);
  files = ctx.keys();
  loadLocaleFile = locale =>
    new Promise(resolve =>
      require.ensure([], () => {
        resolve(ctx(`./b2c-site-${locale}.po`));
      }));
} else {
  const {readdirSync} = require('fs');
  const list = readdirSync(`${__dirname}/../../../../../i18n`);
  files = list.filter(file => /\.po$/.test(file)).map(file => `./${file}`);
  loadLocaleFile = locale =>
    Promise.resolve(require(`../../../../../i18n/b2c-site-${locale}.po`));
}

const CITY_LON = 'UK-LONDON';
const CITY_PAR = 'FR-PARIS';

const CITIES_LIST = {
  FR: CITY_PAR,
  GB: CITY_LON,
};

@withConfig
class I18n {
  get SUPPORTED_LOCALES() { return SUPPORTED_LOCALES; }
  get SUPPORTED_LANGUAGES() { return SUPPORTED_LANGUAGES; }
  get DEFAULT_LOCALES() { return DEFAULT_LOCALES; }
  get SUPPORTED_REGIONS() { return SUPPORTED_REGIONS; }
  get CITIES_LIST() { return CITIES_LIST; }

  negotiateLocale(locale) {
    if (!locale) return null;
    if (SUPPORTED_LOCALES.indexOf(locale) === -1) {
      let [language, region] = locale.split('-');
      language = language && language.toLowerCase();
      region = region && region.toUpperCase();

      if (region === 'AT') {
        locale = 'en-GB';
      } else {
        locale = `${language}-${region}`;
      }

      if (SUPPORTED_LOCALES.indexOf(locale) === -1) {
        if (LANGUAGE_FALLBACKS[language]) {
          locale = LANGUAGE_FALLBACKS[language];
        } else {
          locale = null;
        }
      }
    }

    let checkedLocale;

    if (locale === 'de-AT' || locale === 'en-AT') {
      checkedLocale = 'en-GB';
    } else {
      checkedLocale = locale;
    }

    return checkedLocale;
  }

  sanitizeLocale(locale) {
    locale = this.negotiateLocale(locale) || SUPPORTED_LOCALES[0];

    if (!locale) {
      [locale] = SUPPORTED_LOCALES;
    }

    let actualLocale = locale;
    if (files.indexOf(`./b2c-site-${locale}.po`) === -1) {
      // supported but no file. fallback to language file.
      const [language] = locale.split('-');
      actualLocale = LANGUAGE_FALLBACKS[language];
    }

    if (!actualLocale) {
      [actualLocale] = SUPPORTED_LOCALES;
    }

    return {locale, actualLocale};
  }

  iso3166Region(region) {
    const {config} = this.ctx;

    return config.getValue('locale.iso-3166-country-code', region);
  }

  @observable locale;
  @computed get lc() {
    return this.locale && this.locale.toLowerCase() || '';
  }

  @lazyInitialize
  @nonenumerable
  errors = new I18nErrors(this.ctx);

  @computed get language() {
    if (!this.locale) return null;
    const [language] = this.locale.split('-');
    return language;
  }

  @computed get safeLanguage() {
    if (!this.language) return 'en';
    return this.language;
  }

  @computed get safeRegion() {
    if (!this.locale) return 'GB';
    const [, region] = this.locale.split('-');
    return region;
  }

  @computed get safeLocale() {
    if (!this.lc) return 'en-gb';
    return this.lc;
  }

  @computed get region() {
    if (!this.locale) return null;
    const [, region] = this.locale.split('-');
    return region;
  }

  @computed get umRegion() {
    const {region} = this;

    return region === 'GB' ? 'UK' : region;
  }

  @computed get contentRegion() {
    const {safeLocale} = this;

    return safeLocale.replace('-', '');
  }

  @computed get alternateLangs() {
    if (!this.locale) return null;
    return SUPPORTED_LOCALES.filter(loc => (
      loc !== this.locale
    ));
  }

  baseDomain = __DEV__ ? 'https://localhost:3001' : `https://${HTTP_HOST}`;

  businessBaseDomain = `https://business.${HTTP_HOST}`;

  _pendingLocale = null;
  @async @action
  @suspectInfiniteLoop
  setLocale(locale) {
    let actualLocale;
    ({locale, actualLocale} = this.sanitizeLocale(locale));
    if (locale === this.locale) return null;
    if (locale === this._pendingLocale) return null;

    this.locale = null;

    if (actualLocale === BASE_LANGUAGE) {
      this.dictionary = null;
      this.locale = locale;

      const maxAge = 1200000000;
      this.ctx.setCookie(LANGUAGE_COOKIE_KEY, locale || '', { maxAge, path: '/' });
      return Promise.resolve();
    }


    // TODO this is handling the test cases where we dont want to load the locales when testing a language - an improvement would be to have this handled by webpack in the test setup
    if (process.env && process.env.NODE_ENV === 'test') {
      this.locale = locale;
      this._pendingLocale = null;

      const maxAge = 1200000000;
      this.ctx.setCookie(LANGUAGE_COOKIE_KEY, locale || '', { maxAge, path: '/' });
      return;
    }
    // load locale files
    this._pendingLocale = locale;
    return loadLocaleFile(actualLocale)
      .then(action('loaded dictionary', (dictionary) => {
        this.dictionary = dictionary;
        this.locale = locale;
        this._pendingLocale = null;

        const maxAge = 1200000000;
        this.ctx.setCookie(LANGUAGE_COOKIE_KEY, locale || '', { maxAge, path: '/' });
      }));
  }

  __(string, vars) {
    return translate(this.dictionary, this.locale, string, vars);
  }

  _n(string, string_plural, vars, nkey) {
    return translatePlural(this.dictionary, this.locale, string, string_plural, vars, nkey);
  }

  _x(string, context, vars) {
    return translateContext(this.dictionary, this.locale, string, context, vars);
  }

  _nx(string, string_plural, context, vars, nkey) {
    return translatePluralContext(this.dictionary, this.locale, string, string_plural, context, vars, nkey);
  }

  __raw(string) {
    return translateRaw(this.dictionary, this.locale, string);
  }

  __error(error, fallback) {
    const code = error && (error.code || error.json && error.json.code);
    const message = (this.errors[code] || this.errors[fallback] || this.errors.GENERIC);
    return safe_sprintf(this.locale, message, error);
  }

  __react(string, vars) {
    return translateReact(this.dictionary, this.locale, string, vars);
  }

  __nreact(string, string_plural, vars) {
    return translatePluralReact(this.dictionary, this.locale, string, string_plural, vars);
  }

  __xreact(string, context, vars) {
    return translateContextReact(this.dictionary, this.locale, string, context, vars);
  }

  /** Format an amount of money with the currency sign */
  __money(value, currency) {
    if (value < 0) {
      return this._x('-\xA0%2$s%1$.2f', 'negative amount with currency', [-value, currency]);
    }
    return this._x('%2$s%1$.2f', 'amount with currency', [value, currency]);
  }

  /*
   *
   * Check if it's an error known to us
   *
   */
  isKnownError(error) {
    const code = error && (error.code || (error.json && error.json.code));

    return !!this.errors[code];
  }

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });
  }

  getLocale() {
    let {locale} = this;

    if (!locale) {
      locale = this.negotiateLocale(this.ctx.getCookie(LANGUAGE_COOKIE_KEY));

      if (!locale) {
        if (typeof window !== 'undefined') {
          if (window.navigator && navigator.languages) {
            navigator.languages.some((userLanguage) => {
              locale = this.negotiateLocale(userLanguage);
              return !!locale;
            });
          } else if (window.navigator && navigator.language) {
            locale = this.negotiateLocale(navigator.userLanguage || navigator.language);
          }
        }
      }
    }

    return locale;
  }

  @action setup() {
    let locale = this.getLocale();
    if (locale === BASE_LANGUAGE) return;

    if (!this.dictionary) {
      // force reload
      this.locale = undefined;
    }

    this.setLocale(locale);
  }
}

export default I18n;



// WEBPACK FOOTER //
// ./spa/state/i18n/index.jsx